import type { EffectiveContentResponse } from "@vital/shared";

const LEVEL_LABELS: Record<string, string> = {
  emergency: "اضطراری (L1)",
  site: "سایت",
  site_group: "گروه سایت",
  tenant_global: "سراسر سازمان",
};

const SCOPE_LABELS: Record<string, string> = {
  site: "سایت",
  site_group: "گروه سایت",
  global: "سراسر سازمان",
};

export function resolutionLevelLabel(level: string): string {
  return LEVEL_LABELS[level] ?? level;
}

export function contentScopeLabel(scope: string): string {
  return SCOPE_LABELS[scope] ?? scope;
}

type Winner = NonNullable<EffectiveContentResponse["announcement"]>;

type Props = {
  effective: EffectiveContentResponse | null;
  loading: boolean;
  emptyHint?: string;
};

function WinnerBlock({
  kindLabel,
  winner,
  emptyHint,
}: {
  kindLabel: string;
  winner: Winner | null;
  emptyHint?: string;
}) {
  if (!winner) {
    return (
      <div className="effective-block">
        <h4 className="effective-block__title">{kindLabel}</h4>
        <p className="field-hint">
          {emptyHint ??
            "موردی از CMS برنده نشده — در صورت نیاز پورتال از محتوای محلی استفاده می‌کند."}
        </p>
      </div>
    );
  }

  return (
    <div className="effective-block">
      <h4 className="effective-block__title">{kindLabel}</h4>
      <p className="effective-block__winner">
        <strong>{winner.title}</strong>
      </p>
      <p className="announcement-list__meta">
        سطح: {resolutionLevelLabel(winner.level)} · اولویت:{" "}
        <bdi dir="ltr" className="ltr-isolate latin-digits">
          {winner.priority}
        </bdi>{" "}
        · محدوده: {contentScopeLabel(winner.scope)}
      </p>
      <p className="announcement-list__meta">
        شناسه:{" "}
        <bdi dir="ltr" className="ltr-isolate">
          {winner.contentId}
        </bdi>
      </p>
      <p className="field-hint">{winner.reason}</p>
      {winner.consideredAtLevel.length > 1 && (
        <details className="effective-block__details">
          <summary>نامزدهای همین سطح ({winner.consideredAtLevel.length})</summary>
          <ul className="effective-block__candidates">
            {winner.consideredAtLevel.map((c) => (
              <li key={c.contentId}>
                {c.title} — اولویت{" "}
                <bdi dir="ltr" className="ltr-isolate latin-digits">
                  {c.priority}
                </bdi>
                {c.contentId === winner.contentId ? " · برنده" : ""}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

export function EffectiveContentPanel({ effective, loading, emptyHint }: Props) {
  if (loading) {
    return <p className="card__empty">در حال محاسبهٔ محتوای مؤثر…</p>;
  }
  if (!effective) {
    return <p className="card__empty">{emptyHint ?? "یک سایت را برای پیش‌نمایش انتخاب کنید."}</p>;
  }

  return (
    <div className="effective-panel">
      {effective.killSwitchActive && (
        <p className="banner banner--warning" role="status">
          کلید قطع فعال است — برنده‌های CMS در خروجی عمومی تهی می‌شوند؛ پیشنهاد بازگشت به محتوای محلی
          پورتال.
        </p>
      )}
      <p className="announcement-list__meta">
        سایت:{" "}
        <bdi dir="ltr" className="ltr-isolate">
          {effective.siteCode}
        </bdi>
        {effective.localFallbackSuggested && (
          <> · پیشنهاد: بازگشت به محتوای محلی پورتال</>
        )}
      </p>
      <p className="field-hint">
        ترتیب سطح‌ها: اضطراری ← سایت ← گروه سایت ← سراسر سازمان ← محتوای محلی پورتال
      </p>
      <WinnerBlock kindLabel="اطلاعیهٔ مؤثر" winner={effective.announcement} />
      <WinnerBlock kindLabel="پیشنهاد مؤثر" winner={effective.promotion} />
      <WinnerBlock
        kindLabel="آگهی مؤثر"
        winner={effective.sponsored ?? null}
        emptyHint="آگهی برنده نشده — پورتال آگهی محلی ندارد و این جایگاه خالی می‌ماند."
      />
    </div>
  );
}
